import {
  boqCategories,
  buildMail,
  DEFAULT_RFQ_MESSAGE,
  defaultDeadline,
  deliveryLine,
  matches,
  parseNum,
  primaryContact,
  rfqDraftFrom,
  roDate,
  specsFor,
  specsText,
  todayIso,
  type ItemSpecs,
  type MailRfq,
  type Supplier,
  t,
} from '@procumi/shared';
import { useEffect, useState } from 'react';
import { Avatar, SupplierStack } from '../components/Avatar';
import { boqDraftFor, boqMarkDone } from '../lib/boq';
import { PlanPicker, uploadPlans } from '../components/Plans';
import { CofrajHelper } from '../components/CofrajHelper';
import { ErrorBox, Loading } from '../components/StateBox';
import { SupplierDialog } from '../components/SupplierDialog';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { autoSendAllowed, companyName, currentUser } from '../lib/me';
import type { Route } from '../routes';
import { DateInput } from '../components/DateInput';
import { isBuild, swMail } from '../lib/terms';
import { rich } from '../lib/rich';

interface DraftItem {
  key: string;
  desc: string;
  qty: string;
  unit: string;
  /** zilele de chirie, ca text; '' = cumpărare */
  rent: string;
  /** poziția e la chirie (atunci `rent` trebuie completat) */
  renting: boolean;
  /** caracteristicile puse de chestionar; lipsă = se citesc din descriere */
  specs?: ItemSpecs | null;
}

const emptyItem = (): DraftItem => ({
  key: nextKey(),
  desc: '',
  qty: '',
  unit: t('buc'),
  rent: '',
  renting: false,
});

/** Zilele de chirie scrise de om, sau null dacă nu sunt un număr întreg pozitiv. */
function rentDaysOf(i: DraftItem): number | null {
  const d = parseNum(i.rent);
  return d != null && d >= 1 && Number.isInteger(d) ? d : null;
}

/** Categoriile la care furnizorii închiriază de obicei: acolo apare chestionarul. */
const isCofraj = (category: string) => /cofraj/i.test(category);
/** Valoarea din lista de categorii care deschide caseta pentru o categorie nouă (ca la antemăsurătoare). */
const NEW_CATEGORY = '\u0000new';

interface Draft {
  title: string;
  category: string;
  siteId: string;
  deadline: string;
  /** 'AAAA-LL-ZZ' sau '' */
  neededBy: string;
  note: string;
  items: DraftItem[];
  /** furnizorii bifați */
  picked: Record<string, boolean>;
  /** persoana aleasă pentru fiecare furnizor */
  contactBy: Record<string, string>;
  /** categoria pentru care s-au bifat furnizorii */
  pickedCat: string | null;
  /**
   * furnizori din agendă aduși în listă deși nu sunt pe categoria cererii:
   * un distribuitor de HVAC poate avea și cofraje, fără să fie trecut așa
   */
  extra: string[];
  /** furnizorul pentru care se vede previzualizarea */
  previewId: string | null;
  /** începutul mailului; '' = textul obișnuit */
  message: string;
  /** memento automat cu o zi înainte de termen (doar trimise din Procumi) */
  autoRemind: boolean;
  /** necesarele de pe șantier din care pornește cererea */
  requisitionIds: string[];
  /** ce scrie pe ele, pentru banda de sus: număr, autor, nota pentru achiziții */
  fromReqs: { id: string; number: string; author: string; note: string }[];
}

let seq = 0;
const nextKey = () => `it${++seq}`;

function freshDraft(): Draft {
  return {
    title: '',
    category: '',
    siteId: '',
    deadline: todayIso(7),
    neededBy: '',
    note: '',
    items: [emptyItem()],
    picked: {},
    contactBy: {},
    pickedCat: null,
    extra: [],
    previewId: null,
    message: '',
    autoRemind: true,
    requisitionIds: [],
    fromReqs: [],
  };
}

/** Ciorna se păstrează dacă navighezi și revii, ca în demo. */
let stored: Draft | null = null;

export function NewRfqPage({
  go,
  toast,
  signature,
  siteId,
  requisitionIds,
  fromBoq,
}: {
  go: (r: Route) => void;
  toast: Toast;
  /** semnătura omului, din „Profilul meu” */
  signature: string;
  /** șantierul din care s-a pornit cererea, dacă s-a pornit dintr-un șantier */
  siteId?: string;
  /** necesarele din care s-a pornit cererea */
  requisitionIds?: string[];
  /** categoria din antemăsurătoarea citită (lib/boq.ts) */
  fromBoq?: string;
}) {
  const sameReqs = (d: Draft | null) =>
    !!d && !!requisitionIds && d.requisitionIds.join() === requisitionIds.join();
  const [draft, setDraft] = useState<Draft>(() => {
    // Din antemăsurătoare: pozițiile bifate ale categoriei, într-o ciornă nouă.
    const boq = fromBoq ? boqDraftFor(fromBoq) : null;
    if (boq) {
      return {
        ...freshDraft(),
        ...(siteId ? { siteId } : {}),
        title: boq.title,
        category: fromBoq!,
        items: boq.rows.map((r) => ({
          ...emptyItem(),
          desc: r.desc,
          qty: r.qty != null ? String(r.qty).replace('.', ',') : '',
          unit: r.unit,
          renting: r.rentDays != null,
          rent: r.rentDays != null ? String(r.rentDays) : '',
          specs: r.specs ?? undefined,
        })),
      };
    }
    // Din necesar: o ciornă nouă, cu liniile lui (dacă nu e chiar ciorna lui, păstrată).
    if (requisitionIds?.length) return sameReqs(stored) ? stored! : freshDraft();
    const base = stored && !stored.requisitionIds.length ? stored : freshDraft();
    return siteId ? { ...base, siteId } : base;
  });
  const [reqError, setReqError] = useState('');
  const [reqLoading, setReqLoading] = useState(!!requisitionIds?.length && !sameReqs(stored));

  // Liniile, șantierul și data vin din necesar, o singură dată.
  useEffect(() => {
    if (!requisitionIds?.length || !reqLoading) return;
    let alive = true;
    Promise.all(requisitionIds.map((id) => api.requisition(id)))
      .then((reqs) => {
        if (!alive) return;
        const waiting = reqs.find((r) => r.status !== 'open');
        if (waiting) {
          setReqError(
            t('Necesarul {0} nu mai așteaptă: a fost preluat, respins sau retras.', waiting.number),
          );
          return;
        }
        const from = rfqDraftFrom(reqs);
        setDraft({
          ...freshDraft(),
          title: from.title,
          category: from.category,
          siteId: from.siteId,
          neededBy: from.neededBy ?? '',
          // ofertele cu câteva zile înainte de ziua de pe șantier, nu în aceeași zi
          deadline: defaultDeadline(todayIso(), from.neededBy),
          items: from.items.map((i) => ({
            ...emptyItem(),
            desc: i.desc,
            qty: String(i.qty).replace('.', ','),
            unit: i.unit,
            renting: i.rentDays != null,
            rent: i.rentDays != null ? String(i.rentDays) : '',
            specs: i.specs ?? undefined,
          })),
          requisitionIds,
          fromReqs: reqs.map((r) => ({
            id: r.id,
            number: r.number,
            author: r.createdBy?.name ?? '',
            note: r.note,
          })),
        });
      })
      .catch((e) => alive && setReqError(errMsg(e)))
      .finally(() => alive && setReqLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const auto = autoSendAllowed();
  const me = currentUser();
  const [busy, setBusy] = useState(false);
  // planurile așteaptă în browser până se salvează cererea; nu intră în ciorna păstrată
  const [plans, setPlans] = useState<File[]>([]);
  const [adding, setAdding] = useState(false);
  /** căutarea în agendă e deschisă */
  const [fromBook, setFromBook] = useState(false);
  const [query, setQuery] = useState('');
  const {
    data: suppliers,
    setData: setSuppliers,
    error,
    loading,
    reload,
  } = useAsync(() => api.suppliers(), [], 'suppliers');
  const cats = useAsync(() => api.categories(), [], 'categories');
  const sitesQuery = useAsync(() => api.sites(), [], 'sites');
  const sites = sitesQuery.data ?? [];

  useEffect(() => {
    stored = draft;
  }, [draft]);

  // Ciorna trăiește doar în pagină: supraviețuiește navigării prin aplicație,
  // dar nu unui refresh sau tabului închis. Atunci întreabă browserul, cu textul
  // lui — mesajele proprii nu mai sunt afișate de niciun browser.
  const written =
    !!draft.title.trim() || !!draft.note.trim() || draft.items.some((i) => i.desc.trim() || i.qty.trim());
  useEffect(() => {
    if (!written) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // browserele mai vechi întreabă doar dacă `returnValue` e setat
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [written]);

  // Un singur șantier deschis: îl alegem noi, nu-l mai punem să aleagă.
  useEffect(() => {
    if (draft.siteId || sites.length !== 1) return;
    const only = sites[0]!.id;
    setDraft((d) => (d.siteId ? d : { ...d, siteId: only }));
  }, [sites, draft.siteId]);

  // Toată lista: ce folosește firma, apoi ce se potrivește meseriei ei, plus „+ Categorie nouă…”.
  // Nu se alege nimic singur: categoria decide ce furnizori se bifează (Ticket 08, C10).
  const baseCats = boqCategories(cats.data?.used ?? [], cats.data?.suggested ?? []);
  // din necesar sau din antemăsurătoare poate veni o categorie care nu e în listă
  const categoryOptions =
    draft.category && !baseCats.includes(draft.category) ? [...baseCats, draft.category] : baseCats;
  const [namingCat, setNamingCat] = useState<string | null>(null);

  // La schimbarea categoriei, furnizorii din ea se bifează automat, cu persoana
  // propusă implicit pentru fiecare.
  useEffect(() => {
    if (!suppliers || !draft.category || draft.pickedCat === draft.category) return;
    const inCat = suppliers.filter((s) => s.categories.includes(draft.category));
    setDraft((d) => ({
      ...d,
      picked: Object.fromEntries(inCat.map((s) => [s.id, true])),
      contactBy: Object.fromEntries(inCat.map((s) => [s.id, primaryContact(s)?.id ?? ''])),
      pickedCat: d.category,
      // la altă categorie se pornește de la zero, și cu cei aduși din agendă
      extra: [],
      previewId: null,
    }));
  }, [suppliers, draft.category, draft.pickedCat]);

  const inCategory = (s: Supplier) => s.categories.includes(draft.category);
  const cands = [
    ...(suppliers ?? []).filter(inCategory),
    ...draft.extra
      .map((id) => suppliers?.find((s) => s.id === id))
      .filter((s): s is Supplier => !!s && !inCategory(s)),
  ];
  // Restul agendei, pentru „Din agendă”: cine nu e deja în listă, după nume sau categorie
  // (fără diacritice: „otel” găsește „Oțel Prim”).
  const book = (suppliers ?? [])
    .filter((s) => !s.archivedAt && !cands.some((c) => c.id === s.id))
    .filter((s) => matches(`${s.name} ${s.categories.join(' ')}`, query))
    .sort((a, b) => a.name.localeCompare(b.name, 'ro'));
  const picked = cands.filter((s) => draft.picked[s.id]);
  const n = picked.length;

  const contactFor = (s: Supplier) =>
    s.contacts.find((c) => c.id === draft.contactBy[s.id]) ?? primaryContact(s);

  const patch = (p: Partial<Draft>) => setDraft((d) => ({ ...d, ...p }));

  // Furnizorul adăugat de aici intră în listă și e bifat pe loc: l-ai adăugat
  // ca să-i trimiți cererea. Restul ciornei rămâne cum era.
  const onSupplierAdded = (s: Supplier) => {
    setSuppliers((list) => [...(list ?? []), s]);
    setDraft((d) => ({
      ...d,
      picked: { ...d.picked, [s.id]: true },
      contactBy: { ...d.contactBy, [s.id]: primaryContact(s)?.id ?? '' },
    }));
    cats.reload();
  };
  /** Furnizorul din agendă intră în listă bifat, cu persoana propusă implicit. */
  const addFromBook = (s: Supplier) =>
    setDraft((d) => ({
      ...d,
      extra: [...d.extra, s.id],
      picked: { ...d.picked, [s.id]: true },
      contactBy: { ...d.contactBy, [s.id]: primaryContact(s)?.id ?? '' },
    }));

  const updateItem = <K extends keyof DraftItem>(index: number, field: K, value: DraftItem[K]) =>
    setDraft((d) => ({
      ...d,
      // Descrierea schimbată de mână nu mai corespunde chestionarului: caracteristicile
      // se citesc din noul text.
      items: d.items.map((it, i) =>
        i === index ? { ...it, [field]: value, ...(field === 'desc' ? { specs: null } : {}) } : it,
      ),
    }));

  /** Unde ajunge omul după salvare: din antemăsurătoare, înapoi la ea. */
  const done = (id: string) => {
    if (fromBoq) {
      boqMarkDone(fromBoq, id);
      go({ view: 'boq' });
    } else go({ view: 'rfq', id });
  };

  /** `mailNow`: după salvare, mailurile pleacă din Procumi (Echipă, Companie). */
  const send = async (mailNow: boolean) => {
    const items = draft.items
      .filter((i) => i.desc.trim())
      .map((i) => {
        const q = parseNum(i.qty);
        return {
          desc: i.desc.trim(),
          qty: q != null && q > 0 ? q : 1,
          unit: i.unit.trim() || t('buc'),
          rentDays: i.renting ? rentDaysOf(i) : null,
          specs: i.specs ?? null,
        };
      });
    const noDays = draft.items.find((i) => i.desc.trim() && i.renting && rentDaysOf(i) == null);
    if (!draft.title.trim()) return toast(t('Dă un nume cererii'));
    if (!draft.siteId) return toast(t('Alege șantierul'));
    if (!draft.category.trim()) return toast(t('Alege o categorie'));
    if (!items.length) return toast(t('Adaugă cel puțin o poziție'));
    if (noDays) return toast(t('Scrie în câte zile întregi ții la chirie „{0}”', noDays.desc.trim()));
    if (!picked.length) return toast(t('Alege cel puțin un furnizor'));

    setBusy(true);
    try {
      const created = await api.createRfq({
        title: draft.title.trim(),
        category: draft.category.trim(),
        siteId: draft.siteId,
        deadline: draft.deadline,
        neededBy: draft.neededBy || null,
        note: draft.note.trim(),
        items,
        recipients: picked.map((s) => {
          const contact = contactFor(s);
          return { supplierId: s.id, ...(contact ? { contactId: contact.id } : {}) };
        }),
        requisitionIds: draft.requisitionIds,
        message: draft.message.trim() === t(DEFAULT_RFQ_MESSAGE) ? '' : draft.message.trim(),
        autoRemind: draft.autoRemind,
      });
      stored = null;
      // Planurile se pun după ce cererea există și înainte să plece mailurile,
      // ca furnizorii să le primească. Dacă unul nu se pune, mailurile nu pleacă:
      // omul ar trimite o cerere fără planul pe care l-a ales.
      if (plans.length) {
        const up = await uploadPlans(created.id, plans);
        if (up.failed.length) {
          go({ view: 'rfq', id: created.id });
          toast(
            t(
              'Cererea e salvată, dar nu s-au pus: {0}. Pune-le din nou pe pagina cererii{1}.',
              up.failed.join(', '),
              mailNow ? t(', apoi trimite mailurile') : '',
            ),
          );
          return;
        }
      }
      if (!mailNow) {
        done(created.id);
        toast(t('Cerere salvată. Mailurile de trimis sunt gata pentru {0} furnizori.', picked.length));
        return;
      }
      // Cererea e salvată; dacă un mail nu pleacă, se vede pe furnizor și se retrimite de acolo.
      try {
        const r = await api.mailRfq(created.id);
        toast(
          r.failed.length
            ? t(
                'Au plecat {0} mailuri. N-a plecat la: {1}',
                r.sent,
                r.failed.map((f) => f.supplier).join(', '),
              )
            : t('Cererea a plecat la {0} {1}', r.sent, r.sent === 1 ? t('furnizor') : t('furnizori')),
        );
      } catch (e) {
        toast(t('Cererea e salvată, dar mailurile n-au plecat: {0}', errMsg(e)));
      }
      done(created.id);
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  /* previzualizarea mailului, cu valori de exemplu pentru câmpurile goale */
  const previewItems = draft.items
    .filter((i) => i.desc)
    .map((i) => ({
      desc: i.desc,
      qty: parseNum(i.qty) ?? 0,
      unit: i.unit,
      rentDays: i.renting ? (rentDaysOf(i) ?? 0) : null,
    }));
  const chosenSite = sites.find((s) => s.id === draft.siteId);
  const tmp: MailRfq = {
    title: draft.title || t('[denumire cerere]'),
    company: companyName(),
    site: chosenSite ? deliveryLine(chosenSite) : t('[șantier]'),
    deadline: draft.deadline,
    neededBy: draft.neededBy || null,
    note: draft.note,
    message: draft.message,
    items: previewItems.length ? previewItems : [{ desc: t('[poziție]'), qty: 0, unit: t('buc') }],
  };
  const cur = picked.find((s) => s.id === draft.previewId) ?? picked[0];
  const curContact = cur ? contactFor(cur) : undefined;
  const mail = swMail(
    buildMail(tmp, cur ? { name: cur.name, contact: curContact?.name ?? '' } : null, signature),
  );

  const filled = draft.items.filter((i) => i.desc.trim());
  const site = sites.find((x) => x.id === draft.siteId);

  return (
    <>
      <div className="head">
        <div>
          {fromBoq ? (
            <button className="back" onClick={() => go({ view: 'boq' })}>
              {t('‹ Înapoi la antemăsurătoare')}
            </button>
          ) : null}
          <h1>{t('Cerere de ofertă nouă')}</h1>
          <p>
            {t(
              'Scrii cererea o singură dată. Fiecare furnizor ales primește un mail separat, către persoana aleasă pentru el.',
            )}
          </p>
        </div>
        {fromBoq || draft.fromReqs.length || !isBuild() ? null : (
          <div className="acts">
            <button className="btn" onClick={() => go({ view: 'boq' })}>
              <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M9.2 1.8H4.8A1.6 1.6 0 0 0 3.2 3.4v9.2a1.6 1.6 0 0 0 1.6 1.6h6.4a1.6 1.6 0 0 0 1.6-1.6V5.4L9.2 1.8ZM9 2v3.2h3.6M5.8 8.6h4.4M5.8 11h2.8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t('Din antemăsurătoare')}
            </button>
          </div>
        )}
      </div>

      {loading || reqLoading ? <Loading /> : null}
      {!loading && error ? <ErrorBox error={error} onRetry={reload} /> : null}
      {!reqLoading && reqError ? <ErrorBox error={reqError} /> : null}

      {!loading && !error && !reqLoading && !reqError ? (
        <div className="nr-layout">
          <div className="steps">
            {draft.fromReqs.length ? (
              <section className="sheet pad from-req" aria-label={t('Necesarul din care pornește cererea')}>
                <p>
                  <b>
                    {t('Din {0}', draft.fromReqs.length === 1 ? t('necesarul') : t('necesarele'))}
                    {draft.fromReqs.map((r, i) => (
                      <span key={r.id}>
                        {i ? ', ' : ''}
                        <button
                          type="button"
                          className="link"
                          onClick={() => go({ view: 'requisition', id: r.id })}
                        >
                          {r.number}
                        </button>
                        {r.author ? ` (${r.author})` : ''}
                      </span>
                    ))}
                  </b>
                  {t(
                    '. Liniile, șantierul și data sunt copiate; alege categoria și furnizorii. La salvare, {0} în „se cer oferte”, iar cine l-a trimis primește un mail.',
                    draft.fromReqs.length === 1 ? t('necesarul trece') : t('necesarele trec'),
                  )}
                </p>
                {draft.fromReqs
                  .filter((r) => r.note)
                  .map((r) => (
                    <p key={r.id} className="from-req-note">
                      <b>{t('{0}, pentru achiziții:', r.number)}</b> {r.note}
                    </p>
                  ))}
              </section>
            ) : null}
            <section className="sheet pad nr-sec">
              <StepHead
                n={1}
                title={t('Ce cumperi')}
                hint={t('denumirea, șantierul, termenul și pozițiile')}
              />
              <div className="row">
                <label className="f" style={{ gridColumn: '1/-1' }} htmlFor="rfq-title">
                  {t('Denumire cerere')}
                  <input
                    id="rfq-title"
                    className="inp"
                    value={draft.title}
                    placeholder={
                      isBuild()
                        ? t('ex. Echipamente HVAC – bloc P+4')
                        : t('ex. Aprovizionare noiembrie – sediul central')
                    }
                    onChange={(e) => patch({ title: e.target.value })}
                  />
                </label>
                <label className="f" htmlFor="rfq-cat">
                  {t('Categorie')}
                  {namingCat !== null ? (
                    <input
                      id="rfq-cat"
                      className="inp"
                      autoFocus
                      value={namingCat}
                      placeholder={t('Numele categoriei')}
                      maxLength={60}
                      onChange={(e) => setNamingCat(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                        if (e.key === 'Escape') setNamingCat(null);
                      }}
                      onBlur={() => {
                        const name = namingCat.trim();
                        if (name) patch({ category: name });
                        setNamingCat(null);
                      }}
                    />
                  ) : (
                    <select
                      id="rfq-cat"
                      className="inp"
                      value={draft.category}
                      onChange={(e) =>
                        e.target.value === NEW_CATEGORY
                          ? setNamingCat('')
                          : patch({ category: e.target.value })
                      }
                    >
                      {draft.category ? null : (
                        <option value="" disabled>
                          {t('Alege categoria…')}
                        </option>
                      )}
                      {categoryOptions.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                      <option value={NEW_CATEGORY}>{t('+ Categorie nouă…')}</option>
                    </select>
                  )}
                </label>
                <label className="f" htmlFor="rfq-site">
                  {t('Șantier')}
                  <select
                    id="rfq-site"
                    className="inp"
                    value={draft.siteId}
                    onChange={(e) => patch({ siteId: e.target.value })}
                  >
                    <option value="">{t('Alege șantierul…')}</option>
                    {sites.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.locality ? `${s.name} — ${s.locality}` : s.name}
                      </option>
                    ))}
                  </select>
                  {sites.length === 0 && !sitesQuery.loading ? (
                    <span className="hint" style={{ margin: 0 }}>
                      {t('Nu ai niciun șantier deschis. Adaugă unul din secțiunea Șantiere.')}
                    </span>
                  ) : null}
                </label>
                <label className="f" htmlFor="rfq-deadline">
                  {t('Termen răspuns')}
                  <DateInput
                    id="rfq-deadline"
                    value={draft.deadline}
                    onChange={(v) => patch({ deadline: v })}
                  />
                </label>
                <label className="f" htmlFor="rfq-needed">
                  {t('Necesar pe șantier')}
                  <DateInput
                    id="rfq-needed"
                    value={draft.neededBy ?? ''}
                    onChange={(v) =>
                      // termenul de răspuns nu poate fi în ziua de pe șantier sau după ea
                      patch(
                        v && draft.deadline >= v
                          ? { neededBy: v, deadline: defaultDeadline(todayIso(), v) }
                          : { neededBy: v },
                      )
                    }
                  />
                  <span className="hint" style={{ margin: 0 }}>
                    {t('opțional; apare în mail și te avertizează pe panou')}
                  </span>
                </label>
              </div>

              <h3 className="nr-sub">
                {t('Poziții')} <span className="n">{filled.length}</span>
              </h3>
              <table className="items-tbl nr-items">
                <thead>
                  <tr>
                    <th>{t('Produs / specificație')}</th>
                    <th>{t('Cantitate')}</th>
                    <th>{t('UM')}</th>
                    {isBuild() ? <th>{t('Regim')}</th> : null}
                    <th aria-label={t('Șterge')} />
                  </tr>
                </thead>
                <tbody>
                  {draft.items.map((it, k) => (
                    <tr key={it.key}>
                      <td>
                        <input
                          className="inp"
                          value={it.desc}
                          placeholder={t('Produs / specificație')}
                          aria-label={t('Produs')}
                          onChange={(e) => updateItem(k, 'desc', e.target.value)}
                        />
                        {specsText(specsFor(it.desc, it.specs)) ? (
                          <span className="hint" style={{ margin: '4px 0 0' }}>
                            {specsText(specsFor(it.desc, it.specs))}
                            {specsFor(it.desc, it.specs)?.heights
                              ? t(
                                  ' · o singură poziție; pentru cantități separate pe înălțimi, fă câte o poziție',
                                )
                              : ''}
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <input
                          className="inp"
                          inputMode="decimal"
                          value={it.qty}
                          placeholder={t('Cant.')}
                          aria-label={t('Cantitate')}
                          onChange={(e) => updateItem(k, 'qty', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="inp"
                          value={it.unit}
                          aria-label={t('Unitate de măsură')}
                          onChange={(e) => updateItem(k, 'unit', e.target.value)}
                        />
                      </td>
                      {isBuild() ? (
                        <td className="regime">
                          <select
                            className="inp"
                            aria-label={t('Cumpărare sau chirie')}
                            value={it.renting ? 'rent' : 'buy'}
                            onChange={(e) => updateItem(k, 'renting', e.target.value === 'rent')}
                          >
                            <option value="buy">{t('Cumpărare')}</option>
                            <option value="rent">{t('Chirie')}</option>
                          </select>
                          {it.renting ? (
                            <input
                              className="inp"
                              inputMode="numeric"
                              value={it.rent}
                              placeholder={t('zile')}
                              aria-label={t('Durata chiriei, în zile')}
                              onChange={(e) => updateItem(k, 'rent', e.target.value)}
                            />
                          ) : null}
                        </td>
                      ) : null}
                      <td>
                        {draft.items.length > 1 ? (
                          <button
                            className="x"
                            aria-label={t('Șterge poziția')}
                            onClick={() => patch({ items: draft.items.filter((_, i) => i !== k) })}
                          >
                            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                              <path
                                d="M4 4l8 8M12 4l-8 8"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button className="nr-add" onClick={() => patch({ items: [...draft.items, emptyItem()] })}>
                <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                {t('Adaugă poziție')}
              </button>

              {isBuild() && isCofraj(draft.category) ? (
                <CofrajHelper
                  onAdd={(list) =>
                    setDraft((d) => {
                      const added: DraftItem[] = list.map((c) => ({
                        ...emptyItem(),
                        desc: c.desc,
                        qty: String(c.qty).replace('.', ','),
                        unit: c.unit,
                        renting: c.rentDays != null,
                        rent: c.rentDays != null ? String(c.rentDays) : '',
                        specs: c.specs,
                      }));
                      // rândul gol de la început se înlocuiește, nu rămâne în urmă
                      const kept = d.items.filter((i) => i.desc.trim() || i.qty.trim());
                      return { ...d, items: [...kept, ...added] };
                    })
                  }
                />
              ) : null}

              <label className="f" style={{ marginTop: 16 }} htmlFor="rfq-note">
                {t('Mențiuni pentru furnizori (opțional)')}
                <input
                  id="rfq-note"
                  className="inp"
                  value={draft.note}
                  placeholder={t('ex. Acceptăm echivalente cu fișă tehnică')}
                  onChange={(e) => patch({ note: e.target.value })}
                />
              </label>

              <div className="f" style={{ marginTop: 16 }}>
                {t('Planuri și documente (opțional)')}
                <PlanPicker files={plans} onChange={setPlans} toast={toast} />
              </div>
            </section>

            <section className="sheet pad nr-sec">
              <div className="nr-head-row">
                <StepHead
                  n={2}
                  title={t('Cui trimiți')}
                  hint={t(
                    '{0} din {1} furnizori{2}',
                    n,
                    cands.length,
                    draft.extra.length ? '' : ` ${draft.category}`,
                  )}
                />
                <span className="nr-head-acts">
                  <button
                    className="btn small"
                    aria-expanded={fromBook}
                    onClick={() => {
                      setFromBook(!fromBook);
                      setQuery('');
                    }}
                  >
                    {t('Din agendă')}
                  </button>
                  <button className="btn small" onClick={() => setAdding(true)}>
                    {t('Furnizor nou')}
                  </button>
                </span>
              </div>
              {cands.length ? (
                <div className="checks nr-sups">
                  {cands.map((s) => {
                    const chosen = contactFor(s);
                    return (
                      <label key={s.id} className={draft.picked[s.id] ? 'on' : undefined}>
                        <Avatar name={s.name} size="lg" />
                        <input
                          type="checkbox"
                          checked={!!draft.picked[s.id]}
                          onChange={(e) => patch({ picked: { ...draft.picked, [s.id]: e.target.checked } })}
                        />
                        <span className="pick-body">
                          <span>
                            <b>{s.name}</b>
                            {!inCategory(s) ? (
                              <span className="muted" style={{ fontSize: '.86rem' }}>
                                {t(
                                  '· din agendă{0}',
                                  s.categories.length ? `, ${s.categories.join(', ')}` : '',
                                )}
                              </span>
                            ) : s.categories.length > 1 ? (
                              <span className="muted" style={{ fontSize: '.86rem' }}>
                                {' '}
                                · {s.categories.join(', ')}
                              </span>
                            ) : null}
                          </span>
                          {s.contacts.length > 1 ? (
                            <select
                              className="inp"
                              aria-label={t('Persoana de contact la {0}', s.name)}
                              value={chosen?.id ?? ''}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                patch({ contactBy: { ...draft.contactBy, [s.id]: e.target.value } })
                              }
                            >
                              {s.contacts.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name || c.email}
                                  {c.role ? ` — ${c.role}` : ''}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="muted" style={{ fontSize: '.86rem' }}>
                              {chosen?.name ? `${chosen.name} · ` : ''}
                              {chosen?.email}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="empty" style={{ padding: '8px 0' }}>
                  {draft.category
                    ? t(
                        'Nu ai furnizori pe categoria {0}. Adaugă unul cu „Furnizor nou” sau alege unul cu „Din agendă”.',
                        draft.category,
                      )
                    : t('Alege o categorie ca să vezi furnizorii.')}
                </div>
              )}
              {fromBook ? (
                <div className="book">
                  <input
                    className="inp"
                    type="search"
                    autoFocus
                    placeholder={t('Caută în agendă după nume sau categorie')}
                    aria-label={t('Caută în agendă')}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  {book.length ? (
                    <div className="book-list">
                      {book.map((s) => (
                        <button key={s.id} className="book-row" onClick={() => addFromBook(s)}>
                          <span>
                            <b>{s.name}</b>
                            <span className="muted">
                              {s.categories.length ? s.categories.join(', ') : t('fără categorie')}
                            </span>
                          </span>
                          <span className="book-add">{t('Adaugă')}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="hint" style={{ margin: '8px 0 0' }}>
                      {query.trim()
                        ? t(
                            'Niciun furnizor cu „{0}” în agendă. Dacă e nou, apasă „Furnizor nou”.',
                            query.trim(),
                          )
                        : t('Toți furnizorii din agendă sunt deja în listă.')}
                    </p>
                  )}
                </div>
              ) : null}
            </section>

            <section className="sheet pad nr-sec">
              <StepHead n={3} title={t('Mailul')} hint={t('cum îl vede furnizorul')} />
              <label className="f" htmlFor="rfq-msg" style={{ marginBottom: 14 }}>
                {t('Începutul mailului')}
                <textarea
                  id="rfq-msg"
                  className="inp"
                  rows={2}
                  maxLength={2000}
                  value={draft.message || t(DEFAULT_RFQ_MESSAGE)}
                  onChange={(e) => patch({ message: e.target.value })}
                />
                {draft.message && draft.message.trim() !== t(DEFAULT_RFQ_MESSAGE) ? (
                  <button
                    type="button"
                    className="link"
                    style={{ alignSelf: 'flex-start', fontSize: 13 }}
                    onClick={() => patch({ message: '' })}
                  >
                    {t('Pune înapoi textul obișnuit')}
                  </button>
                ) : (
                  <span className="hint" style={{ margin: 0 }}>
                    {t('Scrie-l cum vorbești cu furnizorii; restul mailului se completează singur.')}
                  </span>
                )}
              </label>
              <div>
                {picked.length > 1 ? (
                  <div className="filters" role="group" aria-label={t('Previzualizare pentru')}>
                    {picked.map((s) => (
                      <button
                        key={s.id}
                        aria-pressed={s.id === cur?.id}
                        onClick={() => patch({ previewId: s.id })}
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                ) : null}
                <div className="nr-mail">
                  <dl className="nr-mail-h">
                    <div>
                      <dt>{t('De la')}</dt>
                      <dd>
                        {me.name} · {companyName()}
                      </dd>
                    </div>
                    <div>
                      <dt>{t('Către')}</dt>
                      <dd>
                        {cur ? (
                          <>
                            {curContact?.name || cur.name}{' '}
                            <span className="muted">
                              {'<'}
                              {curContact?.email}
                              {'>'}
                            </span>
                          </>
                        ) : (
                          <span className="muted">{t('alege un furnizor')}</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt>{t('Subiect')}</dt>
                      <dd>
                        <b>{mail.subject}</b>
                      </dd>
                    </div>
                  </dl>
                  <div className="mail">{mail.body}</div>
                </div>
              </div>

              <p className="hint" style={{ marginTop: 10 }}>
                {rich(
                  'Semnătura e a ta, din <b>Profilul meu</b> (clic pe numele tău, jos în stânga): carte de vizită făcută de Procumi sau cea din Outlook.',
                )}
              </p>
            </section>
          </div>
          <aside className="nr-aside" aria-label={t('Rezumatul cererii')}>
            <div className="sheet nr-sum">
              <h2>{t('Rezumat')}</h2>
              <dl>
                <div>
                  <dt>{t('Cererea')}</dt>
                  <dd>{draft.title.trim() || <span className="muted">{t('fără denumire')}</span>}</dd>
                </div>
                <div>
                  <dt>{t('Categorie')}</dt>
                  <dd>{draft.category || <span className="muted">{t('nealeasă')}</span>}</dd>
                </div>
                <div>
                  <dt>{t('Șantier')}</dt>
                  <dd>{site ? site.name : <span className="muted">{t('neales')}</span>}</dd>
                </div>
                <div>
                  <dt>{t('Termen răspuns')}</dt>
                  <dd>{draft.deadline ? roDate(draft.deadline) : <span className="muted">—</span>}</dd>
                </div>
                <div>
                  <dt>{t('Poziții')}</dt>
                  <dd>{filled.length || <span className="muted">{t('niciuna')}</span>}</dd>
                </div>
                <div>
                  <dt>{t('Furnizori')}</dt>
                  <dd>
                    {n ? (
                      <span className="nr-stack">
                        <SupplierStack
                          suppliers={picked.map((x) => ({
                            id: x.id,
                            name: x.name,
                            answered: true,
                          }))}
                        />
                      </span>
                    ) : (
                      <span className="muted">{t('niciunul')}</span>
                    )}
                  </dd>
                </div>
                {plans.length ? (
                  <div>
                    <dt>{t('Planuri')}</dt>
                    <dd>{plans.length}</dd>
                  </div>
                ) : null}
              </dl>
              {auto ? (
                <>
                  <label className="check-line">
                    <input
                      type="checkbox"
                      checked={draft.autoRemind}
                      onChange={(e) => patch({ autoRemind: e.target.checked })}
                    />
                    <span>
                      {t('Memento automat cu o zi înainte de termen, la furnizorii care n-au răspuns')}
                    </span>
                  </label>
                  <div className="send-row">
                    <button className="btn primary" disabled={busy} onClick={() => void send(true)}>
                      {busy
                        ? t('Se trimite…')
                        : t('Trimite la {0} {1}', n, n === 1 ? t('furnizor') : t('furnizori'))}
                    </button>
                    <button className="btn" disabled={busy} onClick={() => void send(false)}>
                      {t('Doar salvează, trimit eu')}
                    </button>
                  </div>
                  <p className="hint send-who">
                    {t(
                      'Pleacă în numele tău, „{0} · {1}”. Când furnizorul apasă „Răspunde”, mailul ajunge la {2}. Mailul are și un buton prin care furnizorul trece prețurile direct în tabelul tău.',
                      me.name,
                      companyName(),
                      me.email,
                    )}
                  </p>
                </>
              ) : (
                <>
                  <div className="send-row">
                    <button className="btn primary" disabled={busy} onClick={() => void send(false)}>
                      {t('Salvează și pregătește mailurile')}
                    </button>
                    <span className="hint" style={{ margin: 0 }}>
                      {t(
                        'Mailurile le trimiți tu, din programul tău de mail. Le găsești gata de copiat pe pagina cererii.',
                      )}
                    </span>
                  </div>
                  <p className="hint send-who">
                    {t(
                      'În pachetele Echipă și Companie, Procumi trimite singur mailurile, în numele tău, și le amintește furnizorilor de termen.',
                    )}
                  </p>
                </>
              )}
            </div>
          </aside>
        </div>
      ) : null}
      {adding ? (
        <SupplierDialog
          suggestions={categoryOptions}
          defaultCategory={draft.category || undefined}
          toast={toast}
          onSaved={onSupplierAdded}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </>
  );
}

/** Capul unei secțiuni: numărul pasului, titlul și, alături, ce intră aici. */
function StepHead({ n, title, hint }: { n: number; title: string; hint?: string }) {
  return (
    <div className="nr-h">
      <span className="nr-n" aria-hidden="true">
        {n}
      </span>
      <h2>{title}</h2>
      {hint ? <span className="nr-hint">{hint}</span> : null}
    </div>
  );
}
